import { TextInput } from "app/components/FormInputs/FormInputs";
import IconCopy from "app/components/Icon/components/IconCopy/IconCopy";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ReactTooltip from "react-tooltip";
import { verify2fa } from "./TwoFactorApi";
import "./TwoFactorSetUp.scss";

type TwoFactorSetupProps = {
  qrCode?: string;
  secretCode?: string;
  onVerify: () => void;
};

// Shows text and a qr code
// The user scans the qr code,
// and submits the 6-digit code to enable 2fa.
// Also an option to copy the secret code to the clipboard.

const TwoFactorSetup = ({
  qrCode,
  secretCode,
  onVerify,
}: TwoFactorSetupProps) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState("");
  const [errors, setErrors] = useState([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const intl = useIntl();

  const handleInput = (id: string, value: string) => {
    if (id === "verificationCode") {
      setVerificationCode(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!verificationCode || verificationCode.length !== 6) {
      setError(
        intl.formatMessage({
          id: "2fa-error-six-digit-code",
          defaultMessage: "Please enter a 6-digit code",
        })
      );
      return;
    }

    try {
      setIsVerifying(true);
      const response = await verify2fa(verificationCode);
      if (response.success) {
        onVerify();
      } else {
        setErrors(response.errorMessages);
      }
    } catch (err) {
      console.log("!!err", err);
      setError(
        intl.formatMessage({
          id: "2fa-invalid-verification-code",
          defaultMessage: "Invalid verification code",
        })
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const copySecretCode = () => {
    if (secretCode) {
      navigator.clipboard.writeText(secretCode);
    }
  };

  return (
    <div className="two-factor-setup-container">
      <h1>
        <FormattedMessage
          id="setup-2fa-title"
          defaultMessage="Setup authenticator app"
        />
      </h1>

      <p>
        <FormattedMessage
          id="setup-2fa-description"
          defaultMessage="Authenticator apps like Google Authenticator generate one-time passwords as a second factor for verifying your identity when prompted during the sign-in process."
        />
      </p>

      <p>
        <FormattedMessage
          id="setup-2fa-scan-instructions"
          defaultMessage="Use an authenticator app to scan the QR code."
        />
      </p>

      <div className="qr-code-container">
        <img
          className="qr-code"
          src={`data:image/png;base64,${qrCode}`}
          alt="QR Code"
        />
      </div>

      <div className="help-text">
        <FormattedMessage
          id="setup-2fa-trouble-scanning"
          defaultMessage="Having trouble scanning? You can utilize the"
        />{" "}
        <span className="setup-key-link" data-tip data-for="setupKeyTooltip">
          <FormattedMessage
            id="setup-2fa-setup-key"
            defaultMessage="setup key"
          />
        </span>
        <ReactTooltip
          id="setupKeyTooltip"
          place="top"
          type="dark"
          effect="solid"
          delayHide={200}
          clickable={true}
        >
          <div className="two-factor-setup-container__tooltip-content">
            <div className="two-factor-setup-container__tooltip-text">
              {secretCode}
            </div>
            <div className="two-factor-setup-container__copy-icon-wrapper">
              <IconCopy onClick={copySecretCode} />
            </div>
          </div>
        </ReactTooltip>{" "}
        <FormattedMessage
          id="setup-2fa-manual-setup"
          defaultMessage="to manually set up your authenticator app."
        />
      </div>

      <form onSubmit={handleSubmit} className="two-factor-setup-form">
        <div className="two-factor-setup__verification-section">
          <TextInput
            type="text"
            id="verificationCode"
            label={{ id: "setup-2fa-verify-label" }}
            onChange={handleInput}
            value={verificationCode}
            maxLength={6}
            disabled={isVerifying}
            error={error}
          />
        </div>
        <div className="two-factor-setup__verify-button-container">
          <button type="submit" className="two-factor-setup__verify-button">
            <FormattedMessage
              id="setup-2fa-verify-button"
              defaultMessage="Verify"
            />
          </button>
        </div>

        {isVerifying ? <FormattedMessage id="verifying" /> : ""}
        {error && <p className="two-factor-setup__error-message">{error}</p>}
        {errors &&
          errors.map((error: string, index: number) => (
            <p key={index} className="two-factor-setup__error-message">
              {error}
            </p>
          ))}
      </form>
    </div>
  );
};

export default TwoFactorSetup;
